import React from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useBackOfficeNotifications } from "../hooks/useBackOfficeNotifications";
import {
  FileText,
  Users,
  BarChart3,
  LogOut,
  Shield,
  ClipboardCheck,
  UserCog,
  ArrowUpRight,
  Home,
  Bell
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const BackOfficeLayout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Real-time notifications hook
  const { newApplicationsCount, escalatedCount } = useBackOfficeNotifications(
    token,
    user?.role,
    true
  );

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "executive": return "bg-purple-100 text-purple-700";
      case "manager": return "bg-blue-100 text-blue-700";
      case "loan_officer": return "bg-green-100 text-green-700";
      case "admin": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  // Get notification count for nav items
  const getNotificationCount = (path) => {
    if (path === "/backoffice/applications") {
      if (user?.role === "loan_officer") return newApplicationsCount;
      if (user?.role === "executive") return escalatedCount;
    }
    return 0;
  };

  const navItems = [
    {
      label: "Applications",
      path: "/backoffice/applications",
      icon: FileText,
      roles: ["loan_officer", "manager", "executive", "admin"]
    },
    {
      label: "Analytics",
      path: "/backoffice/analytics",
      icon: BarChart3,
      roles: ["loan_officer", "manager", "executive", "admin"]
    },
    {
      label: "Manage Users",
      path: "/backoffice/users",
      icon: Users,
      roles: ["admin"]
    }
  ];

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role) || user?.role === "admin"
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#0A2540] text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-9 rounded" />
              </Link>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
                <Shield className="h-4 w-4" />
                <span className="text-sm font-medium">Back Office</span>
              </div>
            </div>

            <nav className="hidden md:flex items-center gap-1">
              {filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                const notifCount = getNotificationCount(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors relative ${
                      isActive 
                        ? "bg-white/20 text-white" 
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                    {notifCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs animate-pulse">
                        {notifCount > 9 ? "9+" : notifCount}
                      </Badge>
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-medium">{user?.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role?.replace("_", " ").toUpperCase()}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-white/80 hover:text-white hover:bg-white/10"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="md:hidden border-t border-white/10 px-4 py-2 flex gap-2 overflow-x-auto">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg whitespace-nowrap text-sm ${
                  isActive 
                    ? "bg-white/20 text-white" 
                    : "text-white/70"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

export default BackOfficeLayout;
